// ============================================================
// SYNCORB Commerce — Mock Data Seed (India-first)
// All data is deterministic so test IDs never change
// ============================================================
import type {
  Tenant, Plan, PlatformUser, MerchantUser, Seller,
  Product, Offer, Customer, Order, OrderItem,
  Invoice, Shipment, Rma, Category, AttributeFamily,
  InventorySource, ShippingMethod, TaxCategory, TaxRate, Channel,
  Company, CompanyUser, Requisition, Quote, PurchaseOrder,
  PosOutlet, PosShift, LedgerEntry, Payout, CmsPage, AuditEvent,
  StockMovement, StockTransfer, Address, Notification, Cart,
} from "../core/types";

// ---- Helpers ----
function id(prefix: string, n: number | string): string {
  return `${prefix}_${n}`;
}
const now = (): string => new Date().toISOString();
const daysAgo = (d: number): string => new Date(Date.now() - d * 86400000).toISOString();

// ---- Plans ----
export const PLANS: Plan[] = [
  {
    id: "plan_starter",
    name: "Starter",
    price: 0,
    interval: "monthly",
    features: { marketplace: false, b2b: false, pos: false, customDomain: false, maxProducts: 50, maxStaff: 2, maxSellers: 0, maxOutlets: 0, transactionFeeBps: 200 },
  },
  {
    id: "plan_growth",
    name: "Growth",
    price: 199900,
    interval: "monthly",
    features: { marketplace: true, b2b: false, pos: true, customDomain: true, maxProducts: 1000, maxStaff: 10, maxSellers: 20, maxOutlets: 2, transactionFeeBps: 150 },
  },
  {
    id: "plan_enterprise",
    name: "Enterprise",
    price: 999900,
    interval: "monthly",
    features: { marketplace: true, b2b: true, pos: true, customDomain: true, maxProducts: 999999, maxStaff: 999, maxSellers: 999, maxOutlets: 50, transactionFeeBps: 75 },
  },
];

// ---- Platform users ----
export const PLATFORM_USERS: PlatformUser[] = [
  { id: "pu_1", name: "Saddam Hussain", email: "saddam@syncorb.in", role: "super_admin", status: "active" },
  { id: "pu_2", name: "Priya Sharma", email: "priya@syncorb.in", role: "platform_ops", status: "active" },
];

// ---- Tenants ----
export const TENANTS: Tenant[] = [
  {
    id: "ten_1",
    name: "Maharaja Bazaar",
    slug: "maharaja-bazaar",
    status: "active",
    mode: "marketplace",
    planId: "plan_enterprise",
    domain: "maharaja-bazaar.syncorb.store",
    logo: "",
    createdAt: daysAgo(120),
    updatedAt: daysAgo(1),
    ownerEmail: "owner@maharajabazaar.com",
    currency: "INR",
    locale: "en",
    countryCode: "IN",
  },
  {
    id: "ten_2",
    name: "EliteKart",
    slug: "elitekart",
    status: "active",
    mode: "store",
    planId: "plan_growth",
    domain: "elitekart.syncorb.store",
    logo: "",
    createdAt: daysAgo(60),
    updatedAt: daysAgo(3),
    ownerEmail: "hello@elitekart.in",
    currency: "INR",
    locale: "en",
    countryCode: "IN",
  },
  {
    id: "ten_3",
    name: "Namma Brands",
    slug: "namma-brands",
    status: "pending_verification",
    mode: "store",
    planId: "plan_starter",
    domain: "namma-brands.syncorb.store",
    logo: "",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(1),
    ownerEmail: "team@nammabrands.in",
    currency: "INR",
    locale: "en",
    countryCode: "IN",
  },
];

// ---- Channels ----
export const CHANNELS: Channel[] = [
  { id: "ch_1", tenantId: "ten_1", code: "main", name: "Maharaja Web", hostname: "maharaja-bazaar.syncorb.store", defaultLocale: "en", baseCurrency: "INR", status: "active", inventorySourceIds: ["src_1", "src_2"] },
  { id: "ch_2", tenantId: "ten_2", code: "main", name: "EliteKart Web", hostname: "elitekart.syncorb.store", defaultLocale: "en", baseCurrency: "INR", status: "active", inventorySourceIds: ["src_3"] },
];

// ---- Merchant users ----
export const MERCHANT_USERS: MerchantUser[] = [
  { id: "mu_1", tenantId: "ten_1", name: "Arun Mehta", email: "arun@maharajabazaar.com", role: "merchant_owner", permissions: ["*"], status: "active" },
  { id: "mu_2", tenantId: "ten_1", name: "Kavitha Reddy", email: "kavitha@maharajabazaar.com", role: "merchant_fulfillment", permissions: ["orders.*", "inventory.*"], status: "active" },
  { id: "mu_3", tenantId: "ten_2", name: "Rajan Pillai", email: "rajan@elitekart.in", role: "merchant_owner", permissions: ["*"], status: "active" },
];

// ---- Inventory sources ----
export const INVENTORY_SOURCES: InventorySource[] = [
  { id: "src_1", tenantId: "ten_1", name: "Mumbai Warehouse", code: "MUM", address: "Andheri East", city: "Mumbai", state: "Maharashtra", status: "active", isPrimary: true },
  { id: "src_2", tenantId: "ten_1", name: "Delhi Hub", code: "DEL", address: "Connaught Place", city: "Delhi", state: "Delhi", status: "active", isPrimary: false },
  { id: "src_3", tenantId: "ten_2", name: "Bengaluru Store", code: "BLR", address: "Koramangala", city: "Bengaluru", state: "Karnataka", status: "active", isPrimary: true },
];

// ---- Categories ----
export const CATEGORIES: Category[] = [
  { id: "cat_1", tenantId: "ten_1", name: "Electronics", slug: "electronics", status: "active", position: 1, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_2", tenantId: "ten_1", name: "Mobiles", slug: "mobiles", parentId: "cat_1", status: "active", position: 1, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_3", tenantId: "ten_1", name: "Laptops", slug: "laptops", parentId: "cat_1", status: "active", position: 2, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_4", tenantId: "ten_1", name: "Fashion", slug: "fashion", status: "active", position: 2, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_5", tenantId: "ten_1", name: "Men's Clothing", slug: "mens-clothing", parentId: "cat_4", status: "active", position: 1, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_6", tenantId: "ten_1", name: "Home & Kitchen", slug: "home-kitchen", status: "active", position: 3, channelIds: ["ch_1"], imageUrl: "" },
  { id: "cat_7", tenantId: "ten_2", name: "Groceries", slug: "groceries", status: "active", position: 1, channelIds: ["ch_2"], imageUrl: "" },
];

// ---- Attribute families ----
export const ATTRIBUTE_FAMILIES: AttributeFamily[] = [
  { id: "fam_1", tenantId: "ten_1", name: "Default", code: "default", attributeIds: ["attr_sku", "attr_name", "attr_price", "attr_desc", "attr_status"] },
  { id: "fam_2", tenantId: "ten_1", name: "Apparel", code: "apparel", attributeIds: ["attr_sku", "attr_name", "attr_price", "attr_desc", "attr_status", "attr_size", "attr_color"] },
  { id: "fam_3", tenantId: "ten_1", name: "Electronics", code: "electronics", attributeIds: ["attr_sku", "attr_name", "attr_price", "attr_desc", "attr_status", "attr_brand", "attr_warranty"] },
];

// ---- Products ----
export const PRODUCTS: Product[] = [
  {
    id: "prod_1", tenantId: "ten_1", name: "OnePlus 12 5G", sku: "OP12-5G-256",
    type: "simple", familyId: "fam_3", status: "published", approvalStatus: "approved",
    price: 6499900, compareAtPrice: 6999900, cost: 5500000,
    description: "Flagship smartphone with Snapdragon 8 Gen 3, 50MP triple camera system, and 100W SUPERVOOC charging.",
    shortDescription: "OnePlus 12 5G — 256GB, Silky Black",
    weight: 220, imageUrls: ["https://placehold.co/400x400/101828/FFFFFF?text=OP12"],
    categoryIds: ["cat_2"], channelIds: ["ch_1"], taxCategoryId: "tax_1",
    manageStock: true, stock: 85, lowStockThreshold: 10,
    createdAt: daysAgo(90), updatedAt: daysAgo(2), tags: ["smartphone", "5g", "flagship"], hsn: "8517",
  },
  {
    id: "prod_2", tenantId: "ten_1", name: "MacBook Air M3", sku: "MBA-M3-16-512",
    type: "simple", familyId: "fam_3", status: "published", approvalStatus: "approved",
    price: 13499900, compareAtPrice: 14999900, cost: 11000000,
    description: "Apple MacBook Air 15-inch with M3 chip, 16GB RAM, 512GB SSD.",
    shortDescription: "MacBook Air M3 — 15\" 16GB/512GB",
    weight: 1510, imageUrls: ["https://placehold.co/400x400/1d2939/FFFFFF?text=MBA"],
    categoryIds: ["cat_3"], channelIds: ["ch_1"], taxCategoryId: "tax_1",
    manageStock: true, stock: 42, lowStockThreshold: 5,
    createdAt: daysAgo(80), updatedAt: daysAgo(1), tags: ["laptop", "apple", "m3"], hsn: "8471",
  },
  {
    id: "prod_3", tenantId: "ten_1", sellerId: "seller_1",
    name: "Allen Solly Formal Shirt", sku: "AS-FS-BLU-M",
    type: "configurable", familyId: "fam_2", status: "published", approvalStatus: "approved",
    price: 179900, compareAtPrice: 249900, cost: 90000,
    description: "Premium formal shirt, 100% cotton, easy-iron finish.",
    shortDescription: "Allen Solly Formal Shirt — Blue",
    weight: 280, imageUrls: ["https://placehold.co/400x400/2947D9/FFFFFF?text=AS"],
    categoryIds: ["cat_5"], channelIds: ["ch_1"], taxCategoryId: "tax_2",
    manageStock: true, stock: 210, lowStockThreshold: 20,
    createdAt: daysAgo(45), updatedAt: daysAgo(3), tags: ["formal", "shirt", "cotton"],
  },
  {
    id: "prod_4", tenantId: "ten_1", sellerId: "seller_2",
    name: "Prestige Induction Cooktop", sku: "PIC-2000W",
    type: "simple", familyId: "fam_1", status: "published", approvalStatus: "approved",
    price: 349900, compareAtPrice: 449900, cost: 220000,
    description: "2000W induction cooktop with 8 preset menus, touch control.",
    shortDescription: "Prestige 2000W Induction Cooktop",
    weight: 1800, imageUrls: ["https://placehold.co/400x400/0D9488/FFFFFF?text=PIC"],
    categoryIds: ["cat_6"], channelIds: ["ch_1"], taxCategoryId: "tax_2",
    manageStock: true, stock: 63, lowStockThreshold: 8,
    createdAt: daysAgo(30), updatedAt: daysAgo(5), tags: ["kitchen", "cooking", "induction"],
  },
  {
    id: "prod_5", tenantId: "ten_1", sellerId: "seller_1",
    name: "Samsung Galaxy S24+", sku: "SGS24P-256-PUR",
    type: "simple", familyId: "fam_3", status: "pending_approval", approvalStatus: "pending",
    price: 7499900, compareAtPrice: 8499900,
    description: "Samsung Galaxy S24+ with Galaxy AI features.",
    shortDescription: "Samsung S24+ 256GB Cobalt Violet",
    weight: 196, imageUrls: ["https://placehold.co/400x400/E4572E/FFFFFF?text=S24"],
    categoryIds: ["cat_2"], channelIds: ["ch_1"], taxCategoryId: "tax_1",
    manageStock: true, stock: 0, lowStockThreshold: 5,
    createdAt: daysAgo(3), updatedAt: daysAgo(1), tags: ["samsung", "smartphone"],
  },
  {
    id: "prod_6", tenantId: "ten_2",
    name: "Tata Salt Premium", sku: "TATALT-1KG",
    type: "simple", familyId: "fam_1", status: "published", approvalStatus: "approved",
    price: 2900, cost: 1800,
    description: "Iodised salt 1kg pack.",
    shortDescription: "Tata Salt Premium 1kg",
    weight: 1000, imageUrls: ["https://placehold.co/400x400/F3F6FA/101828?text=SALT"],
    categoryIds: ["cat_7"], channelIds: ["ch_2"], taxCategoryId: "tax_2",
    manageStock: true, stock: 500, lowStockThreshold: 50,
    createdAt: daysAgo(60), updatedAt: daysAgo(10),
  },
];

// ---- Offers ----
export const OFFERS: Offer[] = [
  { id: "off_1", tenantId: "ten_1", productId: "prod_1", price: 6499900, compareAtPrice: 6999900, stock: 45, status: "active", inventorySourceId: "src_1", condition: "new" },
  { id: "off_2", tenantId: "ten_1", productId: "prod_1", sellerId: "seller_1", price: 6399900, stock: 30, status: "active", inventorySourceId: "src_2", condition: "new", leadTimeDays: 2 },
  { id: "off_3", tenantId: "ten_1", productId: "prod_2", price: 13499900, compareAtPrice: 14999900, stock: 42, status: "active", inventorySourceId: "src_1", condition: "new" },
  { id: "off_4", tenantId: "ten_1", productId: "prod_3", sellerId: "seller_1", price: 179900, compareAtPrice: 249900, stock: 120, status: "active", inventorySourceId: "src_2", condition: "new" },
  { id: "off_5", tenantId: "ten_1", productId: "prod_4", sellerId: "seller_2", price: 349900, compareAtPrice: 449900, stock: 63, status: "active", inventorySourceId: "src_1", condition: "new" },
];

// ---- Sellers ----
export const SELLERS: Seller[] = [
  {
    id: "seller_1", tenantId: "ten_1", name: "TechZone India", slug: "techzone",
    email: "sell@techzone.in", phone: "9876543210", status: "approved",
    commissionBps: 800, commissionOverrideEnabled: true,
    totalProducts: 48, totalOrders: 234,
    pendingBalance: 240000, availableBalance: 180000, paidBalance: 1200000,
    planId: "plan_growth", logo: "", description: "Authorised electronics seller.",
    createdAt: daysAgo(100),
    policies: { return: "7 days return", shipping: "Free shipping above ₹999" },
  },
  {
    id: "seller_2", tenantId: "ten_1", name: "HomeStyle Co.", slug: "homestyle",
    email: "sell@homestyle.in", phone: "9876540001", status: "approved",
    commissionBps: 1000, commissionOverrideEnabled: false,
    totalProducts: 32, totalOrders: 97,
    pendingBalance: 50000, availableBalance: 120000, paidBalance: 600000,
    planId: "plan_growth", logo: "", description: "Kitchen and home essentials.",
    createdAt: daysAgo(75),
  },
  {
    id: "seller_3", tenantId: "ten_1", name: "FashionNest", slug: "fashionnest",
    email: "sell@fashionnest.in", phone: "9876540002", status: "pending",
    commissionBps: 1200, commissionOverrideEnabled: false,
    totalProducts: 0, totalOrders: 0,
    pendingBalance: 0, availableBalance: 0, paidBalance: 0,
    logo: "", description: "Women's fashion brand.",
    createdAt: daysAgo(2),
  },
];

// ---- Ledger entries ----
export const LEDGER_ENTRIES: LedgerEntry[] = [
  { id: "led_1", tenantId: "ten_1", sellerId: "seller_1", orderItemId: "oi_1", type: "sale", amount: 6399900, currency: "INR", description: "Sale order ORD-001", createdAt: daysAgo(20) },
  { id: "led_2", tenantId: "ten_1", sellerId: "seller_1", orderItemId: "oi_1", type: "commission", amount: -511992, currency: "INR", description: "Commission 8% on ORD-001", createdAt: daysAgo(20) },
  { id: "led_3", tenantId: "ten_1", sellerId: "seller_2", orderItemId: "oi_3", type: "sale", amount: 349900, currency: "INR", description: "Sale order ORD-001", createdAt: daysAgo(20) },
  { id: "led_4", tenantId: "ten_1", sellerId: "seller_2", orderItemId: "oi_3", type: "commission", amount: -34990, currency: "INR", description: "Commission 10% on ORD-001", createdAt: daysAgo(20) },
  { id: "led_5", tenantId: "ten_1", sellerId: "seller_1", type: "payout", amount: -150000, currency: "INR", description: "Payout PAY-001", createdAt: daysAgo(10) },
];

// ---- Payouts ----
export const PAYOUTS: Payout[] = [
  { id: "pay_1", tenantId: "ten_1", sellerId: "seller_1", amount: 150000, status: "paid", requestedAt: daysAgo(15), releasedAt: daysAgo(10), releasedBy: "mu_1", reference: "NEFT/20260912/001" },
  { id: "pay_2", tenantId: "ten_1", sellerId: "seller_2", amount: 80000, status: "requested", requestedAt: daysAgo(3) },
];

// ---- Tax ----
export const TAX_CATEGORIES: TaxCategory[] = [
  { id: "tax_1", tenantId: "ten_1", name: "Electronics GST 18%", description: "GST 18% on electronics" },
  { id: "tax_2", tenantId: "ten_1", name: "General GST 12%", description: "GST 12% on general goods" },
];
export const TAX_RATES: TaxRate[] = [
  { id: "tr_1", tenantId: "ten_1", taxCategoryId: "tax_1", country: "IN", rate: 18, isCompound: false },
  { id: "tr_2", tenantId: "ten_1", taxCategoryId: "tax_2", country: "IN", rate: 12, isCompound: false },
];

// ---- Shipping ----
export const SHIPPING_METHODS: ShippingMethod[] = [
  { id: "ship_1", tenantId: "ten_1", name: "Standard Delivery", code: "flat_rate", amount: 4900, status: "active" },
  { id: "ship_2", tenantId: "ten_1", name: "Free Delivery", code: "free", amount: 0, minOrderAmount: 49900, status: "active" },
  { id: "ship_3", tenantId: "ten_1", name: "Store Pickup", code: "pickup", amount: 0, status: "active" },
];

// ---- Customers ----
export const CUSTOMERS: Customer[] = [
  {
    id: "cust_1", tenantId: "ten_1", name: "Rohit Verma", email: "rohit@example.com",
    phone: "9812345678", status: "active", totalOrders: 5, totalSpend: 2450000,
    createdAt: daysAgo(90),
    addresses: [
      { id: "addr_1", name: "Rohit Verma", phone: "9812345678", line1: "42 Linking Road", city: "Mumbai", state: "Maharashtra", pincode: "400050", country: "IN", isDefault: true },
    ],
  },
  {
    id: "cust_2", tenantId: "ten_1", name: "Ananya Singh", email: "ananya@example.com",
    phone: "9887766554", status: "active", totalOrders: 2, totalSpend: 740000,
    createdAt: daysAgo(45),
    addresses: [
      { id: "addr_2", name: "Ananya Singh", phone: "9887766554", line1: "12 MG Road", city: "Bengaluru", state: "Karnataka", pincode: "560001", country: "IN", isDefault: true },
    ],
  },
];

// ---- Sample address ----
const addr1: Address = {
  id: "addr_1", name: "Rohit Verma", phone: "9812345678",
  line1: "42 Linking Road", city: "Mumbai", state: "Maharashtra", pincode: "400050", country: "IN",
};

// ---- Orders ----
const oi1: OrderItem = {
  id: "oi_1", productId: "prod_1", productName: "OnePlus 12 5G", sku: "OP12-5G-256",
  sellerId: "seller_1", sellerName: "TechZone India", offerId: "off_2",
  qty: 1, qtyInvoiced: 1, qtyShipped: 1, qtyRefunded: 0,
  unitPrice: 6399900, taxAmount: 1151982, discountAmount: 0, lineTotal: 6399900,
  fulfillmentStatus: "delivered", imageUrl: "https://placehold.co/60x60/101828/FFFFFF?text=OP",
};
const oi2: OrderItem = {
  id: "oi_2", productId: "prod_2", productName: "MacBook Air M3", sku: "MBA-M3-16-512",
  offerId: "off_3",
  qty: 1, qtyInvoiced: 1, qtyShipped: 0, qtyRefunded: 0,
  unitPrice: 13499900, taxAmount: 2429982, discountAmount: 0, lineTotal: 13499900,
  fulfillmentStatus: "allocated", imageUrl: "https://placehold.co/60x60/1d2939/FFFFFF?text=MBA",
};
const oi3: OrderItem = {
  id: "oi_3", productId: "prod_4", productName: "Prestige Induction Cooktop", sku: "PIC-2000W",
  sellerId: "seller_2", sellerName: "HomeStyle Co.", offerId: "off_5",
  qty: 1, qtyInvoiced: 0, qtyShipped: 0, qtyRefunded: 0,
  unitPrice: 349900, taxAmount: 41988, discountAmount: 0, lineTotal: 349900,
  fulfillmentStatus: "unallocated", imageUrl: "https://placehold.co/60x60/0D9488/FFFFFF?text=PIC",
};

export const ORDERS: Order[] = [
  {
    id: "ord_1", tenantId: "ten_1", channelId: "ch_1", number: "ORD-2026-001", customerId: "cust_1",
    customerName: "Rohit Verma", customerEmail: "rohit@example.com",
    billingAddress: addr1, shippingAddress: addr1,
    items: [oi1, oi3], status: "completed", fulfillmentStatus: "delivered", paymentStatus: "captured",
    paymentMethod: "Razorpay", subtotal: 6749800, shippingAmount: 0,
    taxAmount: 1193970, discountAmount: 0, grandTotal: 6749800,
    source: "web", createdAt: daysAgo(20), updatedAt: daysAgo(18),
    trackingNumber: "DTDC123456789IN", invoiceNumber: "INV-2026-001",
  },
  {
    id: "ord_2", tenantId: "ten_1", channelId: "ch_1", number: "ORD-2026-002", customerId: "cust_2",
    customerName: "Ananya Singh", customerEmail: "ananya@example.com",
    billingAddress: addr1, shippingAddress: addr1,
    items: [oi2], status: "processing", fulfillmentStatus: "allocated", paymentStatus: "captured",
    paymentMethod: "COD", subtotal: 13499900, shippingAmount: 4900,
    taxAmount: 2429982, discountAmount: 0, grandTotal: 13504800,
    source: "web", createdAt: daysAgo(3), updatedAt: daysAgo(2),
  },
  {
    id: "ord_3", tenantId: "ten_1", channelId: "ch_1", number: "ORD-2026-003", customerId: "cust_1",
    customerName: "Rohit Verma", customerEmail: "rohit@example.com",
    billingAddress: addr1, shippingAddress: addr1,
    items: [oi3], status: "pending", fulfillmentStatus: "unallocated", paymentStatus: "requires_action",
    paymentMethod: "Razorpay", subtotal: 349900, shippingAmount: 4900,
    taxAmount: 41988, discountAmount: 0, grandTotal: 354800,
    source: "web", createdAt: daysAgo(1), updatedAt: daysAgo(1),
  },
];

// ---- Invoices ----
export const INVOICES: Invoice[] = [
  {
    id: "inv_1", tenantId: "ten_1", orderId: "ord_1", invoiceNumber: "INV-2026-001",
    sellerId: "seller_1", items: [oi1], subtotal: 6399900, taxAmount: 1151982, grandTotal: 6399900,
    status: "paid", issuedAt: daysAgo(19),
  },
];

// ---- Shipments ----
export const SHIPMENTS: Shipment[] = [
  {
    id: "ship_1", tenantId: "ten_1", orderId: "ord_1", sellerId: "seller_1",
    items: [{ orderItemId: "oi_1", qty: 1 }],
    status: "delivered", carrier: "DTDC", trackingNumber: "DTDC123456789IN",
    inventorySourceId: "src_2", createdAt: daysAgo(19), shippedAt: daysAgo(18), deliveredAt: daysAgo(16),
  },
];

// ---- RMA ----
export const RMAS: Rma[] = [
  {
    id: "rma_1", tenantId: "ten_1", orderId: "ord_1", customerId: "cust_1", sellerId: "seller_1",
    items: [{ orderItemId: "oi_1", qty: 1, reason: "Product defective", condition: "damaged" }],
    status: "under_review", resolution: "",
    notes: "Screen has dead pixels", createdAt: daysAgo(5), updatedAt: daysAgo(4),
  },
];

// ---- B2B Companies ----
export const COMPANIES: Company[] = [
  {
    id: "comp_1", tenantId: "ten_1", name: "Infosys IT Procurement",
    gst: "29AABCI1234A1Z5", email: "procurement@infosys.example",
    phone: "08028520000", address: "Electronics City, Bengaluru",
    status: "active", salesRepId: "mu_1",
    creditLimit: 50000000, availableCredit: 35000000, paymentTermsDays: 30,
    createdAt: daysAgo(50),
  },
  {
    id: "comp_2", tenantId: "ten_1", name: "Wipro Employee Store",
    email: "store@wipro.example", phone: "08028522000",
    address: "Sarjapur Road, Bengaluru", status: "pending",
    creditLimit: 20000000, availableCredit: 20000000, paymentTermsDays: 15,
    createdAt: daysAgo(4),
  },
];

// ---- Company users ----
export const COMPANY_USERS: CompanyUser[] = [
  { id: "cu_1", companyId: "comp_1", customerId: "cust_2", name: "Ananya Singh", email: "ananya@example.com", role: "buyer", status: "active" },
];

// ---- Requisitions ----
export const REQUISITIONS: Requisition[] = [
  {
    id: "req_1", companyId: "comp_1", name: "Q4 Laptop Refresh",
    items: [
      { productId: "prod_2", name: "MacBook Air M3", qty: 10, price: 13499900 },
      { productId: "prod_1", name: "OnePlus 12 5G", qty: 5, price: 6499900 },
    ],
    status: "active", createdAt: daysAgo(8),
  },
];

// ---- Quotes ----
export const QUOTES: Quote[] = [
  {
    id: "q_1", tenantId: "ten_1", companyId: "comp_1", status: "seller_offer",
    items: [{ productId: "prod_2", name: "MacBook Air M3", qty: 10, requestedPrice: 12000000, agreedPrice: 12800000 }],
    notes: "Annual procurement — please share best price",
    expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
    createdAt: daysAgo(5), updatedAt: daysAgo(1),
    messages: [
      { from: "buyer", text: "We need 10 units of MacBook Air M3. Can you offer ₹1,20,000 per unit?", at: daysAgo(5) },
      { from: "seller", text: "Best we can do is ₹1,28,000 per unit for 10 units. Terms: 30 days net.", at: daysAgo(3) },
      { from: "buyer", text: "Can you match ₹1,25,000?", at: daysAgo(1) },
    ],
  },
];

// ---- Purchase orders ----
export const PURCHASE_ORDERS: PurchaseOrder[] = [
  {
    id: "po_1", tenantId: "ten_1", companyId: "comp_1", orderId: "ord_2",
    poNumber: "INF-PO-2026-1001", status: "converted",
    items: [{ productId: "prod_2", name: "MacBook Air M3", qty: 1, price: 13499900 }],
    totalAmount: 13499900, requestedDelivery: new Date(Date.now() + 5 * 86400000).toISOString(),
    notes: "Delivery to Bengaluru office", createdAt: daysAgo(4),
  },
];

// ---- POS outlets ----
export const POS_OUTLETS: PosOutlet[] = [
  { id: "out_1", tenantId: "ten_1", name: "Mumbai Flagship Store", address: "Phoenix Mills, Lower Parel, Mumbai", inventorySourceId: "src_1", status: "active", cashiers: ["posuser_1"] },
  { id: "out_2", tenantId: "ten_1", sellerId: "seller_1", name: "TechZone Delhi Counter", address: "DLF Promenade, Delhi", inventorySourceId: "src_2", status: "active", cashiers: ["posuser_2"] },
];

// ---- POS shifts ----
export const POS_SHIFTS: PosShift[] = [
  {
    id: "shift_1", outletId: "out_1", cashierId: "posuser_1", cashierName: "Preethi Kumar",
    status: "open", openingFloat: 500000, cashSales: 124900, cashRefunds: 0,
    expectedCash: 624900, grossSales: 124900, netSales: 124900, cashIn: 624900, cashOut: 0,
    openedAt: new Date(Date.now() - 4 * 3600000).toISOString(),
    orders: ["pos_ord_1"],
  },
];

// ---- CMS pages ----
export const CMS_PAGES: CmsPage[] = [
  { id: "cms_1", tenantId: "ten_1", channelId: "ch_1", locale: "en", title: "About Us", slug: "about-us", content: "<h1>About Maharaja Bazaar</h1><p>India's premier multi-vendor marketplace.</p>", status: "published", updatedAt: daysAgo(30) },
  { id: "cms_2", tenantId: "ten_1", channelId: "ch_1", locale: "en", title: "Shipping Policy", slug: "shipping", content: "<h1>Shipping Policy</h1><p>We deliver across India.</p>", status: "published", updatedAt: daysAgo(20) },
];

// ---- Audit events ----
export const AUDIT_EVENTS: AuditEvent[] = [
  { id: "ae_1", tenantId: "ten_1", actor: "Arun Mehta", action: "seller.approved", target: "TechZone India", at: daysAgo(100), ip: "103.12.45.67" },
  { id: "ae_2", actor: "Saddam Hussain", action: "tenant.created", target: "Maharaja Bazaar", at: daysAgo(120), ip: "103.12.45.60" },
  { id: "ae_3", actor: "Saddam Hussain", action: "tenant.sign_in_as", target: "Arun Mehta (ten_1)", at: daysAgo(5), ip: "103.12.45.61", metadata: { reason: "Support request SR-001" } },
];

// ---- Notifications ----
export const NOTIFICATIONS: Notification[] = [
  { id: "notif_1", tenantId: "ten_1", userId: "mu_1", type: "order.placed", title: "New Order", body: "Order ORD-001 placed by Rohit Verma — ₹67,498", read: false, createdAt: daysAgo(20), link: "/merchant/orders/ord_1" },
  { id: "notif_2", tenantId: "ten_1", userId: "mu_1", type: "seller.new_product", title: "Product Pending Approval", body: "Samsung Galaxy S24+ submitted by TechZone India", read: false, createdAt: daysAgo(3), link: "/merchant/sellers/seller_1/products" },
  { id: "notif_3", tenantId: "ten_1", userId: "seller_1", type: "payout.paid", title: "Payout Released", body: "₹1,500 payout PAY-001 has been released to your account", read: true, createdAt: daysAgo(10) },
];

// ---- Cart (active) ----
export const ACTIVE_CART: Cart = {
  id: "cart_1", tenantId: "ten_1", channelId: "ch_1", customerId: "cust_1",
  items: [
    {
      id: "ci_1", offerId: "off_2", productId: "prod_1", productName: "OnePlus 12 5G",
      sku: "OP12-5G-256", sellerId: "seller_1", sellerName: "TechZone India",
      qty: 1, unitPrice: 6399900, lineTotal: 6399900,
      imageUrl: "https://placehold.co/60x60/101828/FFFFFF?text=OP",
    },
    {
      id: "ci_2", offerId: "off_5", productId: "prod_4", productName: "Prestige Induction Cooktop",
      sku: "PIC-2000W", sellerId: "seller_2", sellerName: "HomeStyle Co.",
      qty: 1, unitPrice: 349900, lineTotal: 349900,
      imageUrl: "https://placehold.co/60x60/0D9488/FFFFFF?text=PIC",
    },
  ],
  discountAmount: 0, subtotal: 6749800, shippingAmount: 0,
  taxAmount: 1193970, grandTotal: 6749800,
  createdAt: daysAgo(0), updatedAt: daysAgo(0),
};

// ---- Stock movements ----
export const STOCK_MOVEMENTS: StockMovement[] = [
  { id: "sm_1", tenantId: "ten_1", sourceId: "src_1", productId: "prod_1", type: "opening", qty: 100, by: "System", at: daysAgo(90) },
  { id: "sm_2", tenantId: "ten_1", sourceId: "src_1", productId: "prod_1", type: "sale", qty: -15, reference: "ord_1", by: "System", at: daysAgo(20) },
  { id: "sm_3", tenantId: "ten_1", sourceId: "src_1", productId: "prod_2", type: "opening", qty: 50, by: "System", at: daysAgo(80) },
  { id: "sm_4", tenantId: "ten_1", sourceId: "src_1", productId: "prod_2", type: "sale", qty: -8, reference: "ord_2", by: "System", at: daysAgo(3) },
];

// ---- Stock transfers ----
export const STOCK_TRANSFERS: StockTransfer[] = [
  {
    id: "tr_1", tenantId: "ten_1", fromSourceId: "src_1", toSourceId: "src_2",
    items: [{ productId: "prod_1", name: "OnePlus 12 5G", qty: 20 }],
    status: "received", createdAt: daysAgo(30), updatedAt: daysAgo(28),
  },
];
